import { useQuery } from '@tanstack/react-query';
import {
  getBilling,
  getKey,
  getMe,
  getPermissionChain,
  getUsage,
  listApiTokens,
  listContainers,
  listFirewalls,
  listKeys,
} from './endpoints';
import { isRyptError } from './errors';
import { qk } from './query-keys';
import { currentPeriodUtc } from '../lib/period';
import { inGoodStanding, type Billing } from './types';

/** Who this session is, and which tenant it acts as. Provisioned on first call. */
export function useMe() {
  return useQuery({
    queryKey: qk.me(),
    queryFn: ({ signal }) => getMe(signal),
    // Identity does not change while a tab is open, and re-fetching it on every focus
    // would make the cheapest possible call the most frequent one.
    staleTime: 10 * 60_000,
    refetchOnWindowFocus: false,
  });
}

export function useKeys() {
  return useQuery({
    queryKey: qk.keys.list(),
    queryFn: ({ signal }) => listKeys(signal),
    staleTime: 30_000,
  });
}

/**
 * Whether a key's read answered 404, which for a key its page has drawn means it has been
 * destroyed. That answer is final and outranks `data`: a failed refetch keeps the last
 * data, so a key destroyed while its page was cached would otherwise still be drawn, as
 * pending and offering Restore, from the copy read before it went.
 */
export const keyDestroyed = (error: unknown): boolean => isRyptError(error) && error.code === 'not_found';

export function useKey(keyId: string | undefined) {
  return useQuery({
    queryKey: qk.keys.detail(keyId ?? ''),
    queryFn: ({ signal }) => getKey(keyId!, signal),
    enabled: Boolean(keyId),
    staleTime: 30_000,
  });
}

export function useApiTokens() {
  return useQuery({
    queryKey: qk.apiTokens.list(),
    queryFn: ({ signal }) => listApiTokens(signal),
    staleTime: 30_000,
  });
}

/** The tenant's containers, the default first. Every page about a container's contents
 *  waits on this to know the container is real. */
export function useContainers() {
  return useQuery({
    queryKey: qk.containers(),
    queryFn: ({ signal }) => listContainers(signal),
    staleTime: 30_000,
  });
}

/** Every container's firewalls; a page filters by `container_id`. */
/** A container's permission chain head. */
export function usePermissionChain(containerId: string) {
  return useQuery({
    queryKey: qk.permissionChain(containerId),
    queryFn: ({ signal }) => getPermissionChain(containerId, signal),
    staleTime: 30_000,
  });
}

export function useFirewalls() {
  return useQuery({
    queryKey: qk.firewalls(),
    queryFn: ({ signal }) => listFirewalls(signal),
    staleTime: 30_000,
  });
}

export function useUsage(period: string) {
  const isCurrent = period === currentPeriodUtc();
  return useQuery({
    queryKey: qk.usage(period),
    queryFn: ({ signal }) => getUsage(period, signal),
    // A closed month cannot change. Caching it forever is correct, not an optimisation.
    staleTime: isCurrent ? 60_000 : Infinity,
    refetchOnWindowFocus: isCurrent,
  });
}

/** How often the return from Checkout reads the standing while it waits for Stripe. */
export const BILLING_POLL_MS = 2_000;

/**
 * Whether a billing read has answered what the return from Checkout waits for: a
 * subscription in good standing, or `null`, where billing is not enabled and there is
 * nothing to wait for.
 */
export const subscriptionConfirmed = (data: Billing | null | undefined): boolean =>
  data === null || (data !== undefined && inGoodStanding(data.status));

/**
 * The tenant's billing standing and this month's estimate.
 *
 * `null` means the API has no billing configured: `/v1/billing` is not a route there and
 * answers 404, and every tier is open to every tenant. That is a fact about the
 * deployment, so it is cached like data — with an error state instead, every focus would
 * ask again, and the 404 would reach the screen as `not_found`'s "no such key".
 * `undefined` (still loading) and an error both mean "not known", and nothing is gated
 * on either: the API refuses what it must, and says why.
 *
 * `pollWhile` is for the return from Checkout, which lands before Stripe's webhook has
 * necessarily reached rypt. While it answers true, the standing is read every two
 * seconds until it is good. The interval is this observer's alone, so the Overview and
 * the Keys list never poll.
 */
export function useBilling({ pollWhile }: { pollWhile?: () => boolean } = {}) {
  return useQuery({
    queryKey: qk.billing(),
    queryFn: async ({ signal }): Promise<Billing | null> => {
      try {
        return await getBilling(signal);
      } catch (e) {
        if (isRyptError(e) && e.status === 404) return null;
        throw e;
      }
    },
    staleTime: 30_000,
    // Tests the RAW data in query state, as the first-encrypt watch does: `select` output
    // is never written back to the query, and an interval reading it would poll for ever.
    ...(pollWhile
      ? {
          refetchInterval: (query: { state: { data: Billing | null | undefined } }) =>
            pollWhile() && !subscriptionConfirmed(query.state.data) ? BILLING_POLL_MS : false,
        }
      : {}),
  });
}
